import React, { useEffect, useState } from "react";
import AdminLayout from "src/layout/admin";
import GenericDataLayout from "src/components/custom/GenericDataLayout";
import GenericTable from "src/components/custom/GenericTable";
import Modal from "src/components/modal";
import SearchableSelect from "src/components/custom/SearchableSelect";
import Button from "src/components/custom/Button";
import {
  getMethodApiCall,
  postMethodApiCall,
  deleteMethodApiCall,
} from "src/services/api";
import { toast } from "react-toastify";

const Mapping = () => {
  const [tableRows, setTableRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
  });

  const fetchMappingList = async (page = pagination.page, limit = pagination.limit) => {
    setListLoading(true);
    try {
      const res = await getMethodApiCall("/v1/api/mcc/iti-trade-industry", {
        page,
        limit,
      });
      if (res?.status === 200 && res?.data?.success) {
        setTableRows(res.data.data || []);
        const p = res.data.pagination || {};
        setPagination((prev) => ({
          ...prev,
          page: p.page ?? page,
          limit: p.limit ?? limit,
          totalRecords: p.total ?? 0,
        }));
      } else {
        setTableRows([]);
        toast.error(res?.data?.message || "Failed to load mapping list");
      }
    } catch (e) {
      setTableRows([]);
      toast.error("Failed to load mapping list");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchMappingList(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    fetchMappingList(newPage, pagination.limit);
  };

  const handleLimitChange = (newLimit) => {
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
    fetchMappingList(1, newLimit);
  };

  // --- Add modal ---
  const [itinameOptions, setItinameOptions] = useState([]);
  const [tradenameOptions, setTradenameOptions] = useState([]);
  const [industrynameOptions, setIndustrynameOptions] = useState([]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalTab, setAddModalTab] = useState("add"); // "add" | "delete"
  const [districtOptions, setDistrictOptions] = useState([]);
  const [addForm, setAddForm] = useState(() => ({
    district: "",
    itiname: "",
    tradename: "",
    industryname: "",
  }));

  // Delete tab state (kept separate from Add tab)
  const [deleteForm, setDeleteForm] = useState(() => ({
    district: "",
    itiname: "",
    tradename: "",
    industryname: "",
  }));
  const [deleteItinameOptions, setDeleteItinameOptions] = useState([]);
  const [deleteTradenameOptions, setDeleteTradenameOptions] = useState([]);
  const [deleteIndustryOptions, setDeleteIndustryOptions] = useState([]);

  useEffect(() => {
    const fetchDistricts = async () => {
      const res = await getMethodApiCall("/v1/api/trainee/get_district_picklist");
      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.district)
          .map((item) => ({
            label: item.district,
            value: item.district,
          }));
        setDistrictOptions(options);
      }
    };

    fetchDistricts();
  }, []);

  const openAddModal = () => {
    setAddForm({
      district: "",
      itiname: "",
      tradename: "",
      industryname: "",
    });
    setItinameOptions([]);
    setDeleteForm({
      district: "",
      itiname: "",
      tradename: "",
      industryname: "",
    });
    setDeleteItinameOptions([]);
    setDeleteTradenameOptions([]);
    setDeleteIndustryOptions([]);
    setAddModalTab("add");
    setAddModalOpen(true);
  };

  const closeAddModal = () => setAddModalOpen(false);

  const clearAddForm = () => {
    setAddForm({
      district: "",
      itiname: "",
      tradename: "",
      industryname: "",
    });
    setItinameOptions([]);
    setTradenameOptions([]);
  };

  const clearDeleteForm = () => {
    setDeleteForm({
      district: "",
      itiname: "",
      tradename: "",
      industryname: "",
    });
    setDeleteItinameOptions([]);
    setDeleteTradenameOptions([]);
    setDeleteIndustryOptions([]);
  };

  useEffect(() => {
    const fetchItisByDistrict = async () => {
      if (!addForm.district) {
        setItinameOptions([]);
        setTradenameOptions([]);
        setAddForm((prev) => ({ ...prev, itiname: "", tradename: "", industryname: "" }));
        return;
      }

      const res = await getMethodApiCall(
        `/v1/api/trainee/get_iti_picklist/${encodeURIComponent(addForm.district)}`
      );

      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.iti_name)
          .map((item) => ({
            label: item.iti_name,
            value: item.id,
          }));

        setItinameOptions(options);
      } else {
        setItinameOptions([]);
        setTradenameOptions([]);
        setAddForm((prev) => ({ ...prev, itiname: "", tradename: "", industryname: "" }));
      }
    };

    if (addModalOpen) {
      fetchItisByDistrict();
    }
  }, [addForm.district, addModalOpen]);

  useEffect(() => {
    const fetchTradesByIti = async () => {
      if (!addForm.itiname) {
        setTradenameOptions([]);
        setAddForm((prev) => ({ ...prev, tradename: "" }));
        return;
      }

      const res = await getMethodApiCall(
        `/v1/api/trainee/trades_by_iti/${addForm.itiname}`
      );

      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.trade_name)
          .map((item) => ({
            label: item.trade_name,
            value: item.id,
          }));
        setTradenameOptions(options);
      } else {
        setTradenameOptions([]);
        setAddForm((prev) => ({ ...prev, tradename: "" }));
      }
    };

    if (addModalOpen) {
      fetchTradesByIti();
    }
  }, [addForm.itiname, addModalOpen]);

  useEffect(() => {
    const fetchIndustriesFromMaster = async () => {
      const res = await getMethodApiCall(
        `/v1/api/trainee/get_industry_picklist`
      );

      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.industry_name)
          .map((item) => ({
            label: item.industry_name,
            value: item.id,
          }));
        setIndustrynameOptions(options);
      } else {
        setIndustrynameOptions([]);
      }
    };

    if (addModalOpen) {
      fetchIndustriesFromMaster();
    }
  }, [addModalOpen]);

  // ---- Delete tab cascading dropdowns ----
  useEffect(() => {
    if (!addModalOpen || addModalTab !== "delete") return;

    const fetchItisByDistrict = async () => {
      if (!deleteForm.district) {
        setDeleteItinameOptions([]);
        setDeleteTradenameOptions([]);
        setDeleteIndustryOptions([]);
        setDeleteForm((prev) => ({
          ...prev,
          itiname: "",
          tradename: "",
          industryname: "",
        }));
        return;
      }

      const res = await getMethodApiCall(
        `/v1/api/trainee/get_iti_picklist/${encodeURIComponent(deleteForm.district)}`
      );

      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.iti_name)
          .map((item) => ({
            label: item.iti_name,
            value: item.id,
          }));
        setDeleteItinameOptions(options);
      } else {
        setDeleteItinameOptions([]);
        setDeleteTradenameOptions([]);
        setDeleteIndustryOptions([]);
        setDeleteForm((prev) => ({
          ...prev,
          itiname: "",
          tradename: "",
          industryname: "",
        }));
      }
    };

    fetchItisByDistrict();
  }, [deleteForm.district, addModalOpen, addModalTab]);

  useEffect(() => {
    if (!addModalOpen || addModalTab !== "delete") return;

    const fetchTradesByIti = async () => {
      if (!deleteForm.itiname) {
        setDeleteTradenameOptions([]);
        setDeleteIndustryOptions([]);
        setDeleteForm((prev) => ({
          ...prev,
          tradename: "",
          industryname: "",
        }));
        return;
      }

      const res = await getMethodApiCall(
        `/v1/api/trainee/trades_by_iti/${deleteForm.itiname}`
      );

      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.trade_name)
          .map((item) => ({
            label: item.trade_name,
            value: item.id,
          }));
        setDeleteTradenameOptions(options);
      } else {
        setDeleteTradenameOptions([]);
        setDeleteIndustryOptions([]);
        setDeleteForm((prev) => ({
          ...prev,
          tradename: "",
          industryname: "",
        }));
      }
    };

    fetchTradesByIti();
  }, [deleteForm.itiname, addModalOpen, addModalTab]);

  useEffect(() => {
    if (!addModalOpen || addModalTab !== "delete") return;

    const fetchIndustriesByTradeAndITI = async () => {
      if (!deleteForm.itiname || !deleteForm.tradename) {
        setDeleteIndustryOptions([]);
        setDeleteForm((prev) => ({ ...prev, industryname: "" }));
        return;
      }

      // Controller: GET /industries_by_trade_and_iti/:tradeId/:iti_id
      const res = await getMethodApiCall(
        `/v1/api/trainee/industries_by_trade_and_iti/${encodeURIComponent(
          deleteForm.tradename
        )}/${encodeURIComponent(deleteForm.itiname)}`
      );

      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.industry_name)
          .map((item) => ({
            label: item.industry_name,
            value: item.id,
          }));
        setDeleteIndustryOptions(options);
      } else {
        setDeleteIndustryOptions([]);
        setDeleteForm((prev) => ({ ...prev, industryname: "" }));
      }
    };

    fetchIndustriesByTradeAndITI();
  }, [deleteForm.itiname, deleteForm.tradename, addModalOpen, addModalTab]);

  const handleCreate = async () => {
    if (!addForm.itiname || !addForm.tradename || !addForm.industryname) {
      toast.error("Please select ITI, Trade and Industry");
      return;
    }

    // Step 1: verify selected ITI-Trade relation still exists.
    const tradeCheckRes = await getMethodApiCall(
      `/v1/api/trainee/trades_by_iti/${addForm.itiname}`
    );

    const hasTradeRelation =
      tradeCheckRes?.status === 200 &&
      tradeCheckRes?.data?.success &&
      (tradeCheckRes.data.data || []).some((t) => Number(t.id) === Number(addForm.tradename));

    if (!hasTradeRelation) {
      toast.error("Selected ITI and Trade relation does not exist");
      return;
    }

    // Step 2: create ITI-Trade-Industry relation with location_required=true.
    const createRes = await postMethodApiCall("/v1/api/mcc/iti-trade-industry", {
      iti_id: Number(addForm.itiname),
      trade_id: Number(addForm.tradename),
      industry_id: Number(addForm.industryname),
      location_required: true,
    });

    if (!(createRes?.status === 201 && createRes?.data?.success)) {
      const errorMessage =
        createRes?.data?.message ||
        createRes?.data?.error ||
        "Failed to create ITI-Trade-Industry relation";
      toast.error(errorMessage);
      return;
    }

    toast.success(createRes?.data?.message || "Relation created successfully");
    clearAddForm();
    await fetchMappingList(1, pagination.limit);
  };

  const actions = [
    {
      label: "Action",
      onClick: (e) => {
        e.preventDefault?.();
        openAddModal();
      },
    },
  ];

  // --- Delete ---
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const handleDeleteFromTab = () => {
    if (!deleteForm.itiname || !deleteForm.tradename || !deleteForm.industryname) {
      toast.error("Please select ITI, Trade and Industry");
      return;
    }

    const iti_id = Number(deleteForm.itiname);
    const trade_id = Number(deleteForm.tradename);
    const industry_id = Number(deleteForm.industryname);

    if (!iti_id || !trade_id || !industry_id) {
      toast.error("Invalid selection: missing relation ids");
      return;
    }

    setDeleteSubmitting(true);
    deleteMethodApiCall("/v1/api/mcc/iti-trade-industry", {
      iti_id,
      trade_id,
      industry_id,
    })
      .then((res) => {
        if (res?.status === 200 && res?.data?.success) {
          toast.success(res?.data?.message || "Deleted successfully");
          clearDeleteForm();
          return fetchMappingList(pagination.page, pagination.limit);
        }
        toast.error(
          res?.data?.message ||
            res?.data?.error ||
            "Failed to delete relation",
        );
      })
      .catch(() => {
        toast.error("Failed to delete relation");
      })
      .finally(() => {
        setDeleteSubmitting(false);
      });
  };

  const tableColumns = [
    { header: "DISTRICT", accessor: "district" },
    { header: "ITINAME", accessor: "itiname" },
    { header: "TRADENAME", accessor: "tradename" },
    { header: "INDUSTRYNAME", accessor: "industryname" },
  ];

  return (
    <AdminLayout>
      <GenericDataLayout
        title="Mapping"
        subtitle=""
        actions={actions}
        filters={[]}
        onResetFilters={() => {}}
        isLoading={listLoading}
        pagination={{
          page: pagination.page,
          limit: pagination.limit,
          totalRecords: pagination.totalRecords,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      >
        <GenericTable
          columns={tableColumns}
          data={tableRows}
          pagination={pagination}
        />
      </GenericDataLayout>

      <Modal isOpen={addModalOpen} onClose={closeAddModal} title="Add Mapping">
        <div className="mb-5">
          <div className="flex gap-3 border-b border-gray-200">
            <button
              type="button"
              onClick={() => setAddModalTab("add")}
              className={`pb-3 text-sm font-semibold ${
                addModalTab === "add"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-500 hover:text-gray-700 border-b-2 border-transparent"
              }`}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => setAddModalTab("delete")}
              className={`pb-3 text-sm font-semibold ${
                addModalTab === "delete"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-500 hover:text-gray-700 border-b-2 border-transparent"
              }`}
            >
              Delete
            </button>
          </div>
        </div>

        {addModalTab === "add" ? (
          <>
            <div className="space-y-5">
              <SearchableSelect
                label="Select District"
                placeholder="Select District"
                options={districtOptions}
                value={addForm.district}
                onChange={(v) =>
                  setAddForm((p) => ({
                    ...p,
                    district: v,
                    itiname: "",
                    tradename: "",
                    industryname: "",
                  }))
                }
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <SearchableSelect
                  label="Select ITI"
                  placeholder="Select ITI"
                  options={itinameOptions}
                  value={addForm.itiname}
                  onChange={(v) =>
                    setAddForm((p) => ({ ...p, itiname: v, tradename: "" }))
                  }
                />

                <SearchableSelect
                  label="Select Trade"
                  placeholder="Select Trade"
                  options={tradenameOptions}
                  value={addForm.tradename}
                  onChange={(v) =>
                    setAddForm((p) => ({ ...p, tradename: v }))
                  }
                />
              </div>

              <SearchableSelect
                label="Select Industry"
                placeholder="Select Industry"
                options={industrynameOptions}
                value={addForm.industryname}
                onChange={(v) =>
                  setAddForm((p) => ({ ...p, industryname: v }))
                }
              />
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={clearAddForm}
                className="py-2 px-6 min-w-[8rem] rounded-md border-2 border-primary text-primary font-semibold shadow-sm hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition ease-in duration-200"
              >
                Clear
              </button>
              <Button title="Create" onClick={handleCreate} />
            </div>
          </>
        ) : (
          <div className="space-y-5">
            <SearchableSelect
              label="Select District"
              placeholder="Select District"
              options={districtOptions}
              value={deleteForm.district}
              onChange={(v) =>
                setDeleteForm((p) => ({
                  ...p,
                  district: v,
                  itiname: "",
                  tradename: "",
                  industryname: "",
                }))
              }
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SearchableSelect
                label="Select ITI"
                placeholder="Select ITI"
                options={deleteItinameOptions}
                value={deleteForm.itiname}
                onChange={(v) =>
                  setDeleteForm((p) => ({ ...p, itiname: v, tradename: "", industryname: "" }))
                }
              />

              <SearchableSelect
                label="Select Trade"
                placeholder="Select Trade"
                options={deleteTradenameOptions}
                value={deleteForm.tradename}
                onChange={(v) =>
                  setDeleteForm((p) => ({ ...p, tradename: v, industryname: "" }))
                }
              />
            </div>

            <SearchableSelect
              label="Select Industry"
              placeholder="Select Industry"
              options={deleteIndustryOptions}
              value={deleteForm.industryname}
              onChange={(v) => setDeleteForm((p) => ({ ...p, industryname: v }))}
            />

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={clearDeleteForm}
                disabled={deleteSubmitting}
                className="py-2 px-6 min-w-[8rem] rounded-md border-2 border-primary text-primary font-semibold shadow-sm hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition ease-in duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear
              </button>
              <Button
                title={deleteSubmitting ? "Deleting..." : "Delete"}
                disabled={
                  deleteSubmitting ||
                  !deleteForm.itiname ||
                  !deleteForm.tradename ||
                  !deleteForm.industryname
                }
                onClick={handleDeleteFromTab}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default Mapping;

